// src/components/store/sections/product-cards/OrigamiRibbonProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingCart, Zap, Check } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const ORIGAMI_THEMES = [
  {
    ribbonBg: "bg-gradient-to-r from-rose-600 to-pink-500",
    foldBg: "bg-rose-900",
    trayBg: "from-rose-500 to-pink-600",
    accentText: "text-rose-600",
  },
  {
    ribbonBg: "bg-gradient-to-r from-indigo-600 to-blue-500",
    foldBg: "bg-indigo-950",
    trayBg: "from-indigo-600 to-blue-600",
    accentText: "text-indigo-600",
  },
  {
    ribbonBg: "bg-gradient-to-r from-emerald-600 to-teal-500",
    foldBg: "bg-emerald-950",
    trayBg: "from-emerald-600 to-teal-600",
    accentText: "text-emerald-600",
  },
  {
    ribbonBg: "bg-gradient-to-r from-amber-500 to-orange-500",
    foldBg: "bg-amber-900",
    trayBg: "from-amber-500 to-orange-600",
    accentText: "text-orange-600",
  },
];

export default function OrigamiRibbonProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    description,
    rating,
    currentPrice,
    added,
    handleAddToCart,
    handleBuyNow,
  } = useCardActions(product, subdomain);

  const theme = ORIGAMI_THEMES[index % ORIGAMI_THEMES.length];

  return (
    <div className="relative w-full h-full pt-2 pl-2 select-none group">
      {/* 3D Overhanging Left Origami Price Ribbon */}
      <div className="absolute top-6 left-0 z-20">
        <div
          className={`px-4 py-1.5 ${theme.ribbonBg} text-white font-black text-sm font-mono shadow-lg rounded-r-xl flex items-center gap-1`}
        >
          <span>৳{currentPrice.toLocaleString()}</span>
        </div>
        {/* 3D Paper Fold Triangle Underneath */}
        <div
          className={`w-2.5 h-2.5 ${theme.foldBg}`}
          style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%)" }}
        />
      </div>

      {/* Main Card Body */}
      <div className="w-full h-full bg-white rounded-3xl border border-slate-200/90 shadow-md group-hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col justify-between">
        <div className="p-4 pt-5">
          <div className="flex justify-end mb-1">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
              {categoryName}
            </span>
          </div>

          <Link href={productUrl} className="relative w-full h-40 flex items-center justify-center my-1 block">
            <div className="relative w-36 h-36">
              <Image
                src={productImg}
                alt={productName}
                fill
                unoptimized
                className="object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-lg"
              />
            </div>
          </Link>

          <div className="text-center space-y-1 mt-2">
            <Link href={productUrl} className="block">
              <h3 className={`text-sm font-black uppercase tracking-tight truncate ${theme.accentText}`}>
                {productName}
              </h3>
            </Link>
            <p className="text-[10px] text-slate-400 line-clamp-2 leading-relaxed">
              {description}
            </p>
            <div className="flex items-center justify-center gap-0.5 pt-1 text-amber-400">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-3 h-3 fill-amber-400" />
              ))}
              <span className="text-[10px] text-slate-400 font-bold ml-1">({rating.toFixed(1)})</span>
            </div>
          </div>
        </div>

        {/* Diagonal Origami Folded Bottom Action Tray */}
        <div
          className={`p-4 pt-6 bg-gradient-to-r ${theme.trayBg} flex items-center gap-2`}
          style={{ clipPath: "polygon(0 22%, 100% 0, 100% 100%, 0 100%)" }}
        >
          <button
            type="button"
            onClick={(e) => handleAddToCart(e)}
            className="flex-1 py-2 px-3 rounded-xl bg-white text-slate-900 font-black text-[10px] uppercase tracking-wider shadow-sm hover:bg-slate-50 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-1"
          >
            {added ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <ShoppingCart className="w-3.5 h-3.5" />}
            <span>{added ? "ADDED" : "ADD CART"}</span>
          </button>

          <button
            type="button"
            onClick={(e) => handleBuyNow(e)}
            className="py-2 px-3 rounded-xl bg-black/30 hover:bg-black/50 text-white font-black text-[10px] uppercase tracking-wider active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-1"
          >
            <Zap className="w-3 h-3 fill-amber-300 text-amber-300" />
            <span>BUY</span>
          </button>
        </div>
      </div>
    </div>
  );
}
